import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import type { ProductAccessLevels } from '../types'

import { GetTeamMembers, TEAM_MEMBERS_PAGE_SIZE } from './useTeamMembers'

export const UpdateChannelPermissions = graphql(/* GraphQL */ `
  mutation UpdateChannelPermissions($input: UpdateChannelPermissionsInput!) {
    updateChannelPermissions(input: $input) {
      ... on UpdateChannelPermissionsSuccess {
        teamMember {
          id
          channelPermissions {
            productAccessLevels {
              publish
              comments
            }
            channel {
              id
            }
          }
        }
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

interface ChannelProductAssignment {
  channelId: string
  productAccessLevels: ProductAccessLevels
}

interface UpdateChannelPermissionsInput {
  organizationId: string
  accountId: string
  /** Per-product assignments, sent verbatim. */
  productChannelAssignments: ChannelProductAssignment[]
}

interface UseUpdateChannelPermissionsReturn {
  updateChannelPermissions: (input: UpdateChannelPermissionsInput) => Promise<{
    success: boolean
    error?: string
  }>
  loading: boolean
}

export const useUpdateChannelPermissions =
  (): UseUpdateChannelPermissionsReturn => {
    const [mutate, { loading }] = useMutation(UpdateChannelPermissions)

    const updateChannelPermissions = async (
      input: UpdateChannelPermissionsInput,
    ): Promise<{ success: boolean; error?: string }> => {
      // The backend persists the full per-product object and drops any
      // channel whose every product is `none`.
      const channelAssignments = input.productChannelAssignments.map((ca) => ({
        channelId: ca.channelId,
        productAccessLevels: {
          publish: ca.productAccessLevels.publish,
          comments: ca.productAccessLevels.comments,
        },
      }))

      try {
        const { data } = await mutate({
          variables: {
            input: {
              organizationId: input.organizationId,
              accountId: input.accountId,
              channelAssignments,
            },
          },
          refetchQueries: [
            {
              query: GetTeamMembers,
              variables: {
                input: {
                  organizationId: input.organizationId,
                  first: TEAM_MEMBERS_PAGE_SIZE,
                },
              },
            },
          ],
        })

        if (!data?.updateChannelPermissions) {
          return {
            success: false,
            error: 'Failed to update channel permissions',
          }
        }

        const result = data.updateChannelPermissions

        if (result.__typename === 'UpdateChannelPermissionsSuccess') {
          return { success: true }
        }

        // Handle error responses
        if (result.__typename === 'NotFoundError') {
          return { success: false, error: result.message }
        }

        return { success: false, error: 'Unknown error occurred' }
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'An error occurred',
        }
      }
    }

    return {
      updateChannelPermissions,
      loading,
    }
  }
